import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  LinearProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Checkbox,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import {
  Email as EmailIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
  TableChart as TableChartIcon,
  Group as GroupIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  MeetingRoom as MeetingRoomIcon,
  Hotel as HotelIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { createAssignment, getAssignments, deleteAssignment, exportAssignment, exportAssignmentXlsx, bulkUploadFile, fixIndexes, resendInvitationEmail, seedAssignmentRooms, getAssignmentRooms, getDiscountCodesForAssignment, sendDiscountCodeWhatsApp, getAssignmentDetails, getPaymentStatus, saveAssignmentGroups } from '../../../services/guestAssignmentService';
import { CasaHotelMap, HotelBoutiqueMap } from './RoomMapModal';

const TEMPLATE_COLUMNS = ['evento', 'email', 'nombre', 'telefono'];

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

const WhatsappModalPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 18,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  background: '#ffffff',
  boxShadow: '0 18px 50px rgba(0,0,0,0.12)',
  animation: `${fadeInUp} 0.22s ease-out`,
}));

const AnimatedWhatsappButton = styled(Button)(() => ({
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  borderRadius: 10,
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': { boxShadow: '0 8px 20px rgba(37,211,102,0.20)' },
}));

const AnimatedActionButton = styled(Button)(() => ({
  transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
  borderRadius: 10,
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    boxShadow: '0 8px 20px rgba(212,175,55,0.14)',
    borderColor: '#d4af37',
  },
}));

const CompactField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    fontSize: '0.95rem',
    background: '#ffffff',
  },
  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
  '& .MuiInputBase-input': { padding: '10px 14px' },
}));

const formatPrice = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return `$${numeric.toLocaleString('es-MX')} MXN`;
};

export default function DashboardGuestAssignment() {
  const [assignments, setAssignments] = useState([]);
  const [roomsCount, setRoomsCount] = useState({ casaHotel: 0, boutique: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fixingIndexes, setFixingIndexes] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [newAssignment, setNewAssignment] = useState({
    eventName: '', brideEmail: '', brideName: '',
    bridePhone: '', hotelType: 'all', groomEmail: '',
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);

  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState('');
  const [selectedAssignmentForDiscount, setSelectedAssignmentForDiscount] = useState(null);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [editedWhatsappMessages, setEditedWhatsappMessages] = useState({});
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [dateGroups, setDateGroups] = useState([]);
  const [groupForm, setGroupForm] = useState({
    name: '', start: '', end: '',
    pricing: { perNight: true, casaHotel: {}, boutique: {} },
    selectedGuests: [],
  });
  const [assignmentGuests, setAssignmentGuests] = useState([]);
  const [sendingGroupId, setSendingGroupId] = useState(null);
  const [loadingDiscountCodes, setLoadingDiscountCodes] = useState(false);
  const [sendingDiscount, setSendingDiscount] = useState(false);
  const [expandedAssignments, setExpandedAssignments] = useState({});
  const [assignmentDetailsCache, setAssignmentDetailsCache] = useState({});
  const [paymentStatusCache, setPaymentStatusCache] = useState({});
  
  // Nuevo estado para la pestaña de invitados en el diálogo de grupo
  const [guestTab, setGuestTab] = useState(0); // 0: Casa Hotel, 1: Boutique

  useEffect(() => {
    loadAssignments();
    loadRoomCount();
  }, [page, rowsPerPage, searchTerm]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await getAssignments({ page: page + 1, limit: rowsPerPage, search: searchTerm || undefined });
      setAssignments(data.data || []);
      setTotalAssignments(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message || 'Error al cargar asignaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadRoomCount = async () => {
    try {
      const data = await getAssignmentRooms();
      if (data.success && data.data) {
        const casaHotel = data.data.filter(r => r.type === 'casa_hotel').length;
        const boutique  = data.data.filter(r => r.type === 'boutique').length;
        setRoomsCount({ casaHotel, boutique, total: casaHotel + boutique });
      }
    } catch {
      setRoomsCount({ casaHotel: 0, boutique: 0, total: 0 });
    }
  };

  const handleSeedRooms = async () => {
    setSeeding(true); setError(null);
    try {
      const result = await seedAssignmentRooms();
      setSuccess(`Seed completado: ${result.results?.created} creadas, ${result.results?.updated} actualizadas`);
      loadRoomCount();
    } catch (err) {
      setError(err.message || 'Error al sembrar habitaciones');
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!newAssignment.eventName || !newAssignment.brideEmail || !newAssignment.brideName) {
      setError('Nombre del evento, email y nombre son requeridos'); return;
    }
    setLoading(true); setError(null);
    try {
      const result = await createAssignment({ ...newAssignment });
      setSuccess(`Asignacion "${result.data?.eventName}" creada - Email enviado a ${result.data?.brideEmail}`);
      setDialogOpen(false);
      setNewAssignment({ eventName: '', brideEmail: '', brideName: '', bridePhone: '', hotelType: 'all' });
      loadAssignments();
    } catch (err) {
      setError(err.message || 'Error al crear asignacion');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) { setError('Por favor selecciona un archivo Excel'); return; }
    setUploading(true); setError(null);
    try {
      const result = await bulkUploadFile(selectedFile);
      setSuccess(`${result.results?.created || 0} asignaciones creadas - Emails enviados`);
      setBulkDialogOpen(false); setSelectedFile(null); setBulkPreview([]);
      loadAssignments();
    } catch (err) {
      setError(err.message || 'Error en carga masiva');
    } finally {
      setUploading(false);
    }
  };

  const handleFixIndexes = async () => {
    setFixingIndexes(true); setError(null);
    try {
      await fixIndexes();
      setSuccess('Indices reparados. Ya puedes crear asignaciones.');
      setTabValue(0);
    } catch (err) {
      setError(err.message || 'Error al reparar indices');
    } finally {
      setFixingIndexes(false);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Estas seguro de eliminar esta asignacion?')) return;
    try {
      await deleteAssignment(id);
      setSuccess('Asignacion eliminada'); loadAssignments();
    } catch (err) {
      setError(err.message || 'Error al eliminar asignacion');
    }
  };

  const handleExportAssignment = async (id, eventName) => {
    // Try to capture SVG maps from the page (if present) and send them to backend
    const svgToPngBlob = (svgEl, scale = 2) => new Promise((resolve) => {
      try {
        if (!svgEl) return resolve(null);
        const vb = svgEl.viewBox.baseVal;
        const vbWidth = vb && vb.width ? vb.width : 660;
        const vbHeight = vb && vb.height ? vb.height : 490;

        const clone = svgEl.cloneNode(true);
        clone.setAttribute('width', String(vbWidth));
        clone.setAttribute('height', String(vbHeight));
        clone.removeAttribute('style');

        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(clone);
        if (!svgString.match(/^<svg[^>]+xmlns="http/)) {
          svgString = svgString.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        const svg64 = btoa(unescape(encodeURIComponent(svgString)));
        const imgSrc = 'data:image/svg+xml;base64,' + svg64;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(vbWidth * scale);
            canvas.height = Math.round(vbHeight * scale);
            const ctx = canvas.getContext('2d');
            ctx.setTransform(scale, 0, 0, scale, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, vbWidth, vbHeight);
            canvas.toBlob((blob) => { resolve(blob); }, 'image/png');
          } catch (err) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = imgSrc;
      } catch (err) { resolve(null); }
    });

    try {
      const casaSvg = document.getElementById('hidden-export-svg-casa-hotel');
      const boutiqueSvg = document.getElementById('hidden-export-svg-hotel-boutique');
      let mapCasaBlob = null;
      let mapBoutiqueBlob = null;
      if (casaSvg) mapCasaBlob = await svgToPngBlob(casaSvg, 2);
      if (boutiqueSvg) mapBoutiqueBlob = await svgToPngBlob(boutiqueSvg, 2);

      await exportAssignmentXlsx(id, { mapCasaBlob, mapBoutiqueBlob });
      setSuccess(`${eventName} exportado (XLSX)`);
    } catch (err) {
      // fallback to CSV if xlsx fails
      try { await exportAssignment(id); setSuccess(`${eventName} exportado (CSV)`); }
      catch { setError('Error al exportar'); }
    }
  };

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/guest-assignment/${token}`);
    setSuccess('Link copiado');
  };

  const handleResendEmail = async (id, brideEmail) => {
    setSendingEmail(id); setError(null);
    try {
      await resendInvitationEmail(id);
      setSuccess(`Email reenviado a ${brideEmail}`);
    } catch (err) {
      setError(err.message || 'Error al reenviar email');
    } finally {
      setSendingEmail(null);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const handleDrag = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  }, []);

  const handleFileSelect = (e) => { if (e.target.files?.[0]) processFile(e.target.files[0]); };

  const processFile = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel', 'text/csv',
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Solo se permiten archivos Excel (.xlsx, .xls) o CSV'); return;
    }
    setSelectedFile(file);
    setBulkPreview([{ name: file.name, size: (file.size / 1024).toFixed(2) + ' KB' }]);
    setSuccess(`${file.name} listo para procesar`);
  };

  const clearFile = () => { setSelectedFile(null); setBulkPreview([]); };

  const openDiscountDialog = async (assignment) => {
    setSelectedAssignmentForDiscount(assignment);
    setDiscountDialogOpen(true);
    setLoadingDiscountCodes(true);
    setSelectedDiscountCode('');
    try {
      const result = await getDiscountCodesForAssignment(assignment._id);
      setDiscountCodes(result.data || []);
      try {
        const details = await getAssignmentDetails(assignment._id);
        const allRooms = [...(details.data.casaHotelRooms || []), ...(details.data.boutiqueRooms || [])];
        setAssignmentGuests(allRooms.map(r => ({
          roomId: r.roomId,
          label: `${r.roomId} — ${r.guestName || '(sin huésped)'}`,
          guestName: r.guestName || '',
          guestWhatsapp: r.guestWhatsapp || '',
          roomType: r.roomId?.startsWith('BT') ? 'boutique' : 'casa',
        })));
        setDateGroups(Array.isArray(details.data.dateGroups) ? details.data.dateGroups : []);
      } catch {
        setAssignmentGuests([]); setDateGroups([]);
      }
    } catch {
      setError('Error al cargar los códigos de descuento'); setDiscountCodes([]);
    } finally {
      setLoadingDiscountCodes(false);
    }
  };

  const handleWhatsappLinksGenerated = (links = []) => {
    setWhatsappLinks(links);
    setEditedWhatsappMessages(links.reduce((acc, l) => { acc[l.phone] = l.message || ''; return acc; }, {}));
  };

  const getBookingLink = (link) =>
    link.bookingLink || link.message?.match(/https?:\/\/[^\s]+/)?.[0] || '';

  const getFullWhatsappMessage = (link) => {
    const msg = editedWhatsappMessages[link.phone] || link.message || '';
    const url = getBookingLink(link);
    if (!url) return msg;
    return msg.includes(url) ? msg : `${msg}\n\n${url}`;
  };

  const getWhatsappOpenLink = (link) =>
    `https://wa.me/${link.phone}?text=${encodeURIComponent(getFullWhatsappMessage(link))}`;

  const handleUpdateEditedMessage = (phone, message) =>
    setEditedWhatsappMessages(prev => ({ ...prev, [phone]: message }));

  const handleSendDiscountCode = async () => {
    if (!selectedDiscountCode && dateGroups.length === 0) {
      setError('Por favor selecciona un código de descuento o crea al menos un grupo de fechas'); return;
    }
    setSendingDiscount(true); setError(null);
    try {
      const result = await sendDiscountCodeWhatsApp(
        selectedAssignmentForDiscount._id,
        selectedDiscountCode || null, null,
        dateGroups.length > 0 ? dateGroups : null
      );
      if (result.results?.links?.length > 0) {
        handleWhatsappLinksGenerated(result.results.links);
        setSuccess(`Se generaron ${result.results.links.length} enlaces de WhatsApp`);
      } else {
        setSuccess(`Código enviado a ${result.results?.sent || 0} huéspedes`);
        setDiscountDialogOpen(false); setSelectedDiscountCode(''); setSelectedAssignmentForDiscount(null);
      }
    } catch (err) {
      setError(err.message || 'Error al enviar el código de descuento');
    } finally {
      setSendingDiscount(false);
    }
  };

  const openGroupsDialog = () => {
    setGroupForm({ name: '', start: '', end: '', pricing: { perNight: true, casaHotel: {}, boutique: {} }, selectedGuests: [] });
    setGuestTab(0); // Resetear a la primera pestaña
    setGroupsDialogOpen(true);
  };

  const saveGroup = () => {
    if (!groupForm.name || !groupForm.start || !groupForm.end) {
      setError('Completa nombre y fechas del grupo'); return;
    }
    const newGroup = { ...groupForm, id: Date.now().toString() };
    const newDateGroups = [...dateGroups, newGroup];
    if (selectedAssignmentForDiscount?._id) {
      saveAssignmentGroups(selectedAssignmentForDiscount._id, newDateGroups)
        .then(res => setDateGroups(res?.data?.dateGroups || newDateGroups))
        .catch(err => { setError(err.message || 'Error guardando grupos'); setDateGroups(newDateGroups); });
    } else {
      setDateGroups(newDateGroups);
    }
    setGroupsDialogOpen(false);
  };

  const removeGroup = (groupId) => {
    const newGroups = dateGroups.filter(x => x.id !== groupId);
    if (selectedAssignmentForDiscount?._id) {
      saveAssignmentGroups(selectedAssignmentForDiscount._id, newGroups)
        .then(res => setDateGroups(res?.data?.dateGroups || newGroups))
        .catch(err => { setError(err.message || 'Error eliminando grupo'); setDateGroups(newGroups); });
    } else {
      setDateGroups(newGroups);
    }
  };

  const getGuestLabel = (roomId) => {
    const g = assignmentGuests.find(x => x.roomId === roomId);
    if (!g) return roomId;
    return g.guestName ? `${g.roomId} — ${g.guestName}` : `${g.roomId} — (sin huésped)`;
  };

  const handleSendGroup = async (groupId) => {
    const group = dateGroups.find(g => g.id === groupId);
    if (!group) return;
    setSendingGroupId(groupId); setError(null);
    try {
      const result = await sendDiscountCodeWhatsApp(
        selectedAssignmentForDiscount._id, selectedDiscountCode || null, null, [group]
      );
      if (result.results?.links) handleWhatsappLinksGenerated(result.results.links);
      setSuccess(`Envío de grupo "${group.name}" completado`);
    } catch (err) {
      setError(err.message || 'Error enviando grupo');
    } finally {
      setSendingGroupId(null);
    }
  };

  const handleSendAllGroups = async () => {
    if (dateGroups.length === 0) { setError('No hay grupos para enviar'); return; }
    setSendingDiscount(true); setError(null);
    try {
      const result = await sendDiscountCodeWhatsApp(
        selectedAssignmentForDiscount._id, selectedDiscountCode || null, null, dateGroups
      );
      if (result.results?.links) handleWhatsappLinksGenerated(result.results.links);
      setSuccess(`Envío de ${dateGroups.length} grupos completado`);
    } catch (err) {
      setError(err.message || 'Error enviando grupos');
    } finally {
      setSendingDiscount(false);
    }
  };

  const setPricingField = (hotel, type, value) =>
    setGroupForm(f => ({
      ...f,
      pricing: { ...f.pricing, [hotel]: { ...f.pricing[hotel], [type]: value } },
    }));

  // Filtrar invitados por tipo
  const casaHotelGuests = assignmentGuests.filter(g => g.roomType === 'casa');
  const boutiqueGuests = assignmentGuests.filter(g => g.roomType === 'boutique');

  // Seleccionar/deseleccionar todos en una pestaña específica
  const selectAllInTab = (tabType) => {
    const guestsToSelect = tabType === 'casa' ? casaHotelGuests : boutiqueGuests;
    const allSelected = guestsToSelect.every(g => groupForm.selectedGuests.includes(g.roomId));
    
    if (allSelected) {
      // Deseleccionar todos los de esta pestaña
      const guestIdsToRemove = guestsToSelect.map(g => g.roomId);
      setGroupForm(f => ({
        ...f,
        selectedGuests: f.selectedGuests.filter(id => !guestIdsToRemove.includes(id))
      }));
    } else {
      // Seleccionar todos los de esta pestaña
      const guestIdsToAdd = guestsToSelect.map(g => g.roomId);
      setGroupForm(f => ({
        ...f,
        selectedGuests: [...new Set([...f.selectedGuests, ...guestIdsToAdd])]
      }));
    }
  };

  /* ─────────────────────────────────────── RENDER ─────────────────────────────────────── */
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Alertas globales */}
      {error   && <Alert severity="error"   onClose={() => setError(null)}   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

      {/* ── Header ── */}
      <Paper elevation={0} sx={{ p: 4, mb: 3, backgroundColor: '#ffffff', border: '1px solid #d4af37', borderRadius: 2 }}>
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>
              Asignacion de Habitaciones
            </Typography>
            <Typography variant="body1" sx={{ color: '#666666', mb: 2 }}>
              Gestiona las asignaciones de habitaciones para eventos y bodas
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Chip icon={<GroupIcon sx={{ color: '#1a1a1a !important' }} />} label={`${totalAssignments} Total`}
                sx={{ bgcolor: '#f5f5f5', color: '#1a1a1a', border: '1px solid #e0e0e0', fontWeight: 500 }} />
              <Chip icon={<CheckCircleIcon sx={{ color: '#2e7d32 !important' }} />}
                label={`${assignments.filter(a => a.status === 'completed').length} Completadas`}
                sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', fontWeight: 500 }} />
              <Chip icon={<PendingIcon sx={{ color: '#f57c00 !important' }} />}
                label={`${assignments.filter(a => a.status === 'pending').length} Pendientes`}
                sx={{ bgcolor: '#fff3e0', color: '#f57c00', border: '1px solid #ffe0b2', fontWeight: 500 }} />
              {roomsCount.total > 0 && (
                <Chip icon={<SettingsIcon sx={{ color: '#d4af37 !important' }} />}
                  label={`${roomsCount.total} Habitaciones`}
                  sx={{ bgcolor: '#fff9e6', color: '#d4af37', border: '1px solid #d4af37', fontWeight: 500 }} />
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
            <Stack spacing={1}>
              <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
                sx={{ bgcolor: '#ffffff', '&:hover': { bgcolor: '#a5a5a5' }, fontWeight: 'bold', px: 4, py: 1.5, textTransform: 'none', fontSize: '14px' }}>
                Nueva Asignacion
              </Button>
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setBulkDialogOpen(true)}
                sx={{ borderColor: '#ffffff', color: '#1a1a1a', '&:hover': { borderColor: '#1a1a1a', bgcolor: '#f5f5f5' }, px: 4, py: 1.5, textTransform: 'none', fontSize: '14px' }}>
                Carga Masiva Excel
              </Button>
              <Button variant="outlined" size="small"
                startIcon={seeding ? <CircularProgress size={14} /> : <SettingsIcon />}
                onClick={handleSeedRooms} disabled={seeding}
                sx={{ borderColor: '#d4af37', color: '#d4af37', '&:hover': { borderColor: '#d4af37', bgcolor: '#fff9e6' }, textTransform: 'none', fontSize: '12px' }}>
                {seeding ? 'Sembrando...' : 'Seed Habitaciones'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Tabla de asignaciones ── */}
      <Box sx={{ position: 'absolute', left: -10000, top: -10000, width: 700, height: 540, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div id="hidden-map-export" style={{ width: 700, height: 540 }}>
          <CasaHotelMap soldRooms={[]} idPrefix="hidden-export" />
          <HotelBoutiqueMap soldRooms={[]} idPrefix="hidden-export" />
        </div>
      </Box>
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField size="small" placeholder="Buscar por evento o email..."
            value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: '#999', mr: 1, fontSize: 18 }} /> }}
            sx={{ flex: 1, maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Button startIcon={<RefreshIcon />} onClick={loadAssignments} disabled={loading}
            sx={{ color: '#666', textTransform: 'none' }}>Actualizar</Button>
          <Button startIcon={fixingIndexes ? <CircularProgress size={14} /> : <SettingsIcon />}
            onClick={handleFixIndexes} disabled={fixingIndexes}
            sx={{ color: '#d4af37', textTransform: 'none' }}>
            {fixingIndexes ? 'Reparando...' : 'Reparar índices'}
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ bgcolor: '#fff9e6', '& .MuiLinearProgress-bar': { bgcolor: '#d4af37' } }} />}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Evento</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Contacto</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1a1a1a', textAlign: 'right' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                    No hay asignaciones. Crea una nueva para comenzar.
                  </TableCell>
                </TableRow>
              ) : assignments.map((assignment) => {
                const isExpanded = !!expandedAssignments[assignment._id];
                const details = assignmentDetailsCache[assignment._id];
                const payments = paymentStatusCache[assignment._id];
                return (
                  <React.Fragment key={assignment._id}>
                    <TableRow hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <IconButton size="small" onClick={async () => {
                            // Toggle expand; on expand, load details and payment status if not cached
                            setExpandedAssignments(prev => {
                              const next = { ...prev, [assignment._id]: !prev[assignment._id] };
                              return next;
                            });
                            if (!assignmentDetailsCache[assignment._id]) {
                              try {
                                const d = await getAssignmentDetails(assignment._id);
                                setAssignmentDetailsCache(prev => ({ ...prev, [assignment._id]: d.data }));
                              } catch (err) {
                                setError(err.message || 'Error cargando detalles');
                              }
                            }
                            if (!paymentStatusCache[assignment._id]) {
                              try {
                                const p = await getPaymentStatus(assignment._id);
                                setPaymentStatusCache(prev => ({ ...prev, [assignment._id]: p.data }));
                              } catch (err) {
                                // no bloquear si falla
                              }
                            }
                          }}>
                            {isExpanded ? <TableChartIcon /> : <TableChartIcon />}
                          </IconButton>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>{assignment.eventName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {assignment.casaHotelRooms?.length || 0} Casa · {assignment.boutiqueRooms?.length || 0} Boutique
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{assignment.brideName}</Typography>
                        <Typography variant="caption" color="text.secondary">{assignment.brideEmail}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={assignment.status === 'completed' ? 'Completada' : 'Pendiente'} size="small"
                          sx={{
                            bgcolor: assignment.status === 'completed' ? '#e8f5e9' : '#fff3e0',
                            color:   assignment.status === 'completed' ? '#2e7d32' : '#f57c00',
                            fontWeight: 600,
                          }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{formatDate(assignment.createdAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Copiar link">
                            <IconButton size="small" onClick={() => handleCopyLink(assignment.token)}><LinkIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Reenviar email">
                            <IconButton size="small"
                              onClick={() => handleResendEmail(assignment._id, assignment.brideEmail)}
                              disabled={sendingEmail === assignment._id}>
                              {sendingEmail === assignment._id ? <CircularProgress size={16} /> : <EmailIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar código por WhatsApp">
                            <IconButton size="small" onClick={() => openDiscountDialog(assignment)} sx={{ color: '#25D366' }}>
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Exportar">
                            <IconButton size="small" onClick={() => handleExportAssignment(assignment._id, assignment.eventName)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDeleteAssignment(assignment._id)} sx={{ color: '#d32f2f' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Row detalle: roomlist */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0, borderBottom: '1px solid #eee' }}>
                          <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                            {details ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Habitación</TableCell>
                                    <TableCell>Huésped</TableCell>
                                    <TableCell>WhatsApp</TableCell>
                                    <TableCell align="right">Pagó</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Pendiente</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {[...(details.casaHotelRooms || []), ...(details.boutiqueRooms || [])].map(r => {
                                    const paymentEntry = payments?.guests?.find(g => g.roomId === r.roomId) || null;
                                    const hasPaid = paymentEntry?.hasPaid;
                                    const total = paymentEntry?.booking?.totalPrice ?? 0;
                                    const paid = paymentEntry?.booking?.initialPayment ?? (hasPaid ? total : 0);
                                    const pending = Math.max(0, (total || 0) - (paid || 0));
                                    return (
                                      <TableRow key={r.roomId}>
                                        <TableCell>{r.name} ({r.roomId})</TableCell>
                                        <TableCell>{r.guestName || '—'}</TableCell>
                                        <TableCell>{r.guestWhatsapp || '—'}</TableCell>
                                        <TableCell align="right">{hasPaid ? 'Sí' : 'No'}</TableCell>
                                        <TableCell align="right">{formatPrice(total)}</TableCell>
                                        <TableCell align="right">{formatPrice(pending)}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalAssignments)} de {totalAssignments}
          </Typography>
          <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)} sx={{ textTransform: 'none' }}>Anterior</Button>
          <Button size="small" disabled={(page + 1) * rowsPerPage >= totalAssignments} onClick={() => setPage(p => p + 1)} sx={{ textTransform: 'none' }}>Siguiente</Button>
        </Box>
      </Paper>

      {/* ════════════════════════════════════════
          Diálogo: Nueva Asignación
      ════════════════════════════════════════ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          Nueva Asignación de Habitaciones
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField label="Nombre del evento *" fullWidth value={newAssignment.eventName}
              onChange={(e) => setNewAssignment(p => ({ ...p, eventName: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Nombre del contacto *" fullWidth value={newAssignment.brideName}
              onChange={(e) => setNewAssignment(p => ({ ...p, brideName: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Email del contacto *" type="email" fullWidth value={newAssignment.brideEmail}
              onChange={(e) => setNewAssignment(p => ({ ...p, brideEmail: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Teléfono" fullWidth value={newAssignment.bridePhone}
              onChange={(e) => setNewAssignment(p => ({ ...p, bridePhone: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField label="Email del novio (opcional)" type="email" fullWidth value={newAssignment.groomEmail}
              onChange={(e) => setNewAssignment(p => ({ ...p, groomEmail: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <FormControl fullWidth>
              <InputLabel>Tipo de hotel</InputLabel>
              <Select value={newAssignment.hotelType} label="Tipo de hotel"
                onChange={(e) => setNewAssignment(p => ({ ...p, hotelType: e.target.value }))}
                sx={{ borderRadius: 2 }}>
                <MenuItem value="all">Ambos (Casa Hotel + Boutique)</MenuItem>
                <MenuItem value="casa">Solo Casa Hotel</MenuItem>
                <MenuItem value="boutique">Solo Hotel Boutique</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateAssignment} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ bgcolor: '#ffffff', '&:hover': { bgcolor: '#afafaf' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}>
            {loading ? 'Creando...' : 'Crear y enviar email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════════
          Diálogo: Carga Masiva Excel
      ════════════════════════════════════════ */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          Carga Masiva por Excel
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            El archivo debe contener las columnas: <strong>{TEMPLATE_COLUMNS.join(', ')}</strong>
          </Alert>
          <Box onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => document.getElementById('bulk-file-input').click()}
            sx={{
              border: `2px dashed ${dragActive ? '#d4af37' : '#e0e0e0'}`,
              borderRadius: 3, p: 4, textAlign: 'center',
              bgcolor: dragActive ? '#fff9e6' : '#fafafa',
              cursor: 'pointer', transition: 'all 0.2s ease', mb: 2,
            }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: dragActive ? '#d4af37' : '#ccc', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Arrastra tu archivo aquí o haz clic para seleccionar</Typography>
            <Typography variant="caption" color="text.secondary">.xlsx, .xls, .csv</Typography>
            <input id="bulk-file-input" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileSelect} />
          </Box>
          {bulkPreview.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>{bulkPreview[0].name}</Typography>
                <Typography variant="caption" color="text.secondary">{bulkPreview[0].size}</Typography>
              </Box>
              <IconButton size="small" onClick={clearFile} sx={{ color: '#d32f2f' }}><DeleteIcon fontSize="small" /></IconButton>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => { setBulkDialogOpen(false); clearFile(); }} sx={{ textTransform: 'none', color: '#666' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleBulkUpload} disabled={uploading || !selectedFile}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}>
            {uploading ? 'Cargando...' : 'Subir y procesar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════════
          Diálogo: Enviar código de descuento / WhatsApp
      ════════════════════════════════════════ */}
      <Dialog
        open={discountDialogOpen && whatsappLinks.length === 0}
        onClose={() => { setDiscountDialogOpen(false); setSelectedDiscountCode(''); setSelectedAssignmentForDiscount(null); setDateGroups([]); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <WhatsappModalPaper elevation={0} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, borderRadius: 3, bgcolor: '#f0fbf4', border: '1px solid rgba(37,211,102,0.16)' }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 16, bgcolor: '#e8f5e9', display: 'grid', placeItems: 'center', border: '1px solid rgba(37,211,102,0.16)' }}>
              <WhatsAppIcon sx={{ fontSize: 24, color: '#25D366' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="900" sx={{ fontFamily: 'Georgia, serif' }}>Enviar código de descuento</Typography>
              <Typography variant="body2" color="text.secondary">{selectedAssignmentForDiscount?.eventName}</Typography>
            </Box>
          </Box>

          {loadingDiscountCodes ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#25D366' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Cargando códigos...</Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ p: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Código de descuento (opcional)</InputLabel>
                <Select value={selectedDiscountCode} label="Código de descuento (opcional)"
                  onChange={(e) => setSelectedDiscountCode(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="">— Sin código —</MenuItem>
                  {discountCodes.map((code) => (
                    <MenuItem key={code._id || code.code} value={code.code}>
                      {code.code}{code.discount ? ` · ${code.discount}%` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {dateGroups.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Grupos de fechas ({dateGroups.length})</Typography>
                  <Stack spacing={1}>
                    {dateGroups.map((group) => (
                      <Paper key={group.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>{group.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.start} → {group.end} · {group.selectedGuests?.length || 0} invitado(s)
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" onClick={() => handleSendGroup(group.id)} disabled={sendingGroupId === group.id}
                            startIcon={sendingGroupId === group.id ? <CircularProgress size={12} /> : <WhatsAppIcon />}
                            sx={{ color: '#25D366', textTransform: 'none', fontSize: '11px' }}>Enviar</Button>
                          <IconButton size="small" onClick={() => removeGroup(group.id)} sx={{ color: '#d32f2f' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                  {dateGroups.length > 1 && (
                    <Button fullWidth variant="outlined" onClick={handleSendAllGroups} disabled={sendingDiscount}
                      startIcon={sendingDiscount ? <CircularProgress size={14} /> : <SendIcon />}
                      sx={{ mt: 1, borderRadius: 2, borderColor: '#25D366', color: '#25D366', textTransform: 'none' }}>
                      Enviar todos los grupos
                    </Button>
                  )}
                </Box>
              )}
            </Stack>
          )}

          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.14)', mt: 1 }}>
            <Button onClick={() => { setDiscountDialogOpen(false); setSelectedDiscountCode(''); setSelectedAssignmentForDiscount(null); setDateGroups([]); }}
              sx={{ color: '#666666', borderRadius: 2, px: 2.5, py: 1.2, fontWeight: 700, textTransform: 'none' }}>
              Cancelar
            </Button>
            <AnimatedActionButton variant="outlined" onClick={openGroupsDialog}
              startIcon={<CalendarTodayIcon />} sx={{ borderRadius: 2, px: 2.5, py: 1.2 }}>
              Crear grupo
            </AnimatedActionButton>
            <AnimatedWhatsappButton variant="contained" onClick={handleSendDiscountCode}
              disabled={sendingDiscount || (!selectedDiscountCode && dateGroups.length === 0)}
              startIcon={sendingDiscount ? <CircularProgress size={18} sx={{ color: '#ffffff' }} /> : <WhatsAppIcon />}
              sx={{ borderRadius: 2, px: 3, py: 1.2, bgcolor: '#25D366', '&:hover': { bgcolor: '#20BD5A' }, color: '#ffffff' }}>
              {sendingDiscount ? 'Generando enlaces...' : 'Generar enlaces de WhatsApp'}
            </AnimatedWhatsappButton>
          </DialogActions>
        </WhatsappModalPaper>
      </Dialog>

      {/* ════════════════════════════════════════
          Diálogo: Crear grupo de fechas — CON PESTAÑAS PARA CASA HOTEL Y BOUTIQUE
      ════════════════════════════════════════ */}
      <Dialog
        open={groupsDialogOpen}
        onClose={() => setGroupsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, m: 2, maxHeight: 'calc(100vh - 40px)', overflow: 'hidden' } }}
      >
        <WhatsappModalPaper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ── Header ── */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 3, py: 2.5,
            borderBottom: '1px solid #f0f0f0',
            bgcolor: '#f8fefb',
            flexShrink: 0,
          }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 12, bgcolor: '#e8f5e9',
              display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <CalendarTodayIcon sx={{ fontSize: 22, color: '#25D366' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>
                Crear grupo de fechas
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#aaa' }}>
                Fechas · invitados · precios
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setGroupsDialogOpen(false)} sx={{ color: '#ccc', '&:hover': { color: '#888' } }}>
              <ContentCopyIcon sx={{ fontSize: 17, transform: 'rotate(45deg)' }} />
            </IconButton>
          </Box>

          {/* ── Cuerpo: fila superior (nombre + fechas) ── */}
          <Box sx={{ px: 3, pt: 3, pb: 2, flexShrink: 0, borderBottom: '1px solid #f0f0f0', bgcolor: '#ffffff' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 2 }}>
              <CompactField
                label="Nombre del grupo" fullWidth size="medium"
                value={groupForm.name}
                onChange={(e) => setGroupForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej. Fin de semana largo"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.95rem' } }}
              />
              <CompactField
                type="date" label="Desde" fullWidth size="medium"
                InputLabelProps={{ shrink: true }}
                value={groupForm.start}
                onChange={(e) => setGroupForm(f => ({ ...f, start: e.target.value }))}
              />
              <CompactField
                type="date" label="Hasta" fullWidth size="medium"
                InputLabelProps={{ shrink: true }}
                value={groupForm.end}
                onChange={(e) => setGroupForm(f => ({ ...f, end: e.target.value }))}
              />
            </Box>
          </Box>

          {/* ── Cuerpo: 2 columnas — Invitados (con pestañas) | Precios ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, overflow: 'hidden', bgcolor: '#ffffff' }}>

            {/* ── Columna izquierda: Invitados CON PESTAÑAS ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid #e8e8e8', overflow: 'hidden', bgcolor: '#fafafa' }}>
              
              {/* Tabs para cambiar entre Casa Hotel y Boutique */}
              <Box sx={{ borderBottom: '1px solid #e8e8e8', bgcolor: '#ffffff', flexShrink: 0 }}>
                <Tabs 
                  value={guestTab} 
                  onChange={(e, newValue) => setGuestTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      py: 1.5,
                      minHeight: 'auto',
                    },
                    '& .Mui-selected': {
                      color: '#25D366',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#25D366',
                      height: 3,
                    }
                  }}
                >
                  <Tab 
                    icon={<HomeIcon sx={{ fontSize: 18 }} />} 
                    iconPosition="start"
                    label={`Casa Hotel (${casaHotelGuests.length})`} 
                  />
                  <Tab 
                    icon={<HotelIcon sx={{ fontSize: 18 }} />} 
                    iconPosition="start"
                    label={`Boutique (${boutiqueGuests.length})`} 
                  />
                </Tabs>
              </Box>

              {/* Contenido de la pestaña seleccionada */}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                
                {/* Sub-header de cada pestaña con controles */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  px: 3, 
                  py: 1.5, 
                  borderBottom: '1px solid #e8e8e8', 
                  flexShrink: 0,
                  bgcolor: '#ffffff'
                }}>
                  <Typography sx={{ 
                    fontSize: '12px', 
                    fontWeight: 800, 
                    letterSpacing: '0.1em',
                    color: '#666', 
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <PersonIcon sx={{ fontSize: 16, color: guestTab === 0 ? '#64b5f6' : '#d4af37' }} />
                    INVITADOS
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      onClick={() => selectAllInTab(guestTab === 0 ? 'casa' : 'boutique')}
                      sx={{ 
                        fontSize: '12px', 
                        color: '#25D366', 
                        fontWeight: 600, 
                        cursor: 'pointer',
                        userSelect: 'none', 
                        '&:hover': { color: '#20BD5A' }, 
                        transition: 'color 0.15s' 
                      }}>
                      {(() => {
                        const currentGuests = guestTab === 0 ? casaHotelGuests : boutiqueGuests;
                        const allSelected = currentGuests.length > 0 && currentGuests.every(g => groupForm.selectedGuests.includes(g.roomId));
                        return allSelected ? 'Deseleccionar todos' : 'Seleccionar todos';
                      })()}
                    </Typography>
                    <Box sx={{ 
                      bgcolor: groupForm.selectedGuests.filter(id => {
                        const guest = assignmentGuests.find(g => g.roomId === id);
                        return guest && ((guestTab === 0 && guest.roomType === 'casa') || (guestTab === 1 && guest.roomType === 'boutique'));
                      }).length > 0 ? '#e8f5e9' : '#f5f5f5',
                      border: `1px solid ${groupForm.selectedGuests.filter(id => {
                        const guest = assignmentGuests.find(g => g.roomId === id);
                        return guest && ((guestTab === 0 && guest.roomType === 'casa') || (guestTab === 1 && guest.roomType === 'boutique'));
                      }).length > 0 ? '#25D366' : '#ddd'}`,
                      borderRadius: 6, 
                      px: 1.2, 
                      py: 0.3,
                      transition: 'all 0.2s' 
                    }}>
                      <Typography sx={{ 
                        fontSize: '13px', 
                        fontWeight: 800,
                        color: groupForm.selectedGuests.filter(id => {
                          const guest = assignmentGuests.find(g => g.roomId === id);
                          return guest && ((guestTab === 0 && guest.roomType === 'casa') || (guestTab === 1 && guest.roomType === 'boutique'));
                        }).length > 0 ? '#25D366' : '#999', 
                        lineHeight: 1.4 
                      }}>
                        {groupForm.selectedGuests.filter(id => {
                          const guest = assignmentGuests.find(g => g.roomId === id);
                          return guest && ((guestTab === 0 && guest.roomType === 'casa') || (guestTab === 1 && guest.roomType === 'boutique'));
                        }).length}
                        <span style={{ fontWeight: 500, color: '#bbb' }}>
                          /{(guestTab === 0 ? casaHotelGuests.length : boutiqueGuests.length)}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Lista de invitados según pestaña seleccionada */}
                {guestTab === 0 ? (
                  // Casa Hotel Guests
                  casaHotelGuests.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Alert severity="info" sx={{ borderRadius: 2, fontSize: '13px' }}>
                        No hay habitaciones de Casa Hotel en esta asignación.
                      </Alert>
                    </Box>
                  ) : (
                    <Box sx={{
                      overflowY: 'auto', 
                      flex: 1,
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0', borderRadius: 3 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3, '&:hover': { bgcolor: '#aaa' } },
                    }}>
                      {casaHotelGuests.map((g, idx) => {
                        const isSelected = groupForm.selectedGuests.includes(g.roomId);
                        return (
                          <Box
                            key={g.roomId}
                            onClick={() => setGroupForm(f => ({
                              ...f,
                              selectedGuests: isSelected
                                ? f.selectedGuests.filter(id => id !== g.roomId)
                                : [...f.selectedGuests, g.roomId],
                            }))}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              px: 3, 
                              py: 1.5,
                              bgcolor: isSelected ? '#e8f5e9' : (idx % 2 === 0 ? '#ffffff' : '#fafafa'),
                              borderBottom: '1px solid #efefef',
                              transition: 'all 0.15s ease',
                              userSelect: 'none',
                              '&:hover': { 
                                bgcolor: isSelected ? '#c8e6c9' : '#f5f5f5',
                                transform: 'translateX(2px)'
                              },
                            }}
                          >
                            <Box sx={{
                              width: 20, 
                              height: 20, 
                              borderRadius: '5px', 
                              flexShrink: 0,
                              border: isSelected ? '2px solid #25D366' : '2px solid #ccc',
                              bgcolor: isSelected ? '#25D366' : '#fff',
                              display: 'grid', 
                              placeItems: 'center',
                              transition: 'all 0.12s ease',
                              boxShadow: isSelected ? '0 2px 4px rgba(37,211,102,0.2)' : 'none'
                            }}>
                              {isSelected && (
                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                                  <path d="M1 4.5L4 7.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </Box>
                            
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: '#64b5f6', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
                            
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ 
                                fontSize: '15px', 
                                fontWeight: isSelected ? 700 : 600,
                                color: isSelected ? '#1a5a2e' : '#2a2a2a', 
                                lineHeight: 1.3,
                                mb: 0.3
                              }}>
                                {g.roomId}
                              </Typography>
                              <Typography sx={{ 
                                fontSize: '13px', 
                                color: isSelected ? '#2e7d32' : '#777',
                                lineHeight: 1.3, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}>
                                <PersonIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                                {g.guestName || 'Sin huésped asignado'}
                              </Typography>
                            </Box>
                            
                            <Chip
                              label="Casa Hotel"
                              size="small"
                              sx={{
                                fontSize: '10px',
                                height: 20,
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )
                ) : (
                  // Boutique Guests
                  boutiqueGuests.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Alert severity="info" sx={{ borderRadius: 2, fontSize: '13px' }}>
                        No hay habitaciones de Hotel Boutique en esta asignación.
                      </Alert>
                    </Box>
                  ) : (
                    <Box sx={{
                      overflowY: 'auto', 
                      flex: 1,
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0', borderRadius: 3 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3, '&:hover': { bgcolor: '#aaa' } },
                    }}>
                      {boutiqueGuests.map((g, idx) => {
                        const isSelected = groupForm.selectedGuests.includes(g.roomId);
                        return (
                          <Box
                            key={g.roomId}
                            onClick={() => setGroupForm(f => ({
                              ...f,
                              selectedGuests: isSelected
                                ? f.selectedGuests.filter(id => id !== g.roomId)
                                : [...f.selectedGuests, g.roomId],
                            }))}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              px: 3, 
                              py: 1.5,
                              bgcolor: isSelected ? '#e8f5e9' : (idx % 2 === 0 ? '#ffffff' : '#fafafa'),
                              borderBottom: '1px solid #efefef',
                              transition: 'all 0.15s ease',
                              userSelect: 'none',
                              '&:hover': { 
                                bgcolor: isSelected ? '#c8e6c9' : '#f5f5f5',
                                transform: 'translateX(2px)'
                              },
                            }}
                          >
                            <Box sx={{
                              width: 20, 
                              height: 20, 
                              borderRadius: '5px', 
                              flexShrink: 0,
                              border: isSelected ? '2px solid #25D366' : '2px solid #ccc',
                              bgcolor: isSelected ? '#25D366' : '#fff',
                              display: 'grid', 
                              placeItems: 'center',
                              transition: 'all 0.12s ease',
                              boxShadow: isSelected ? '0 2px 4px rgba(37,211,102,0.2)' : 'none'
                            }}>
                              {isSelected && (
                                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                                  <path d="M1 4.5L4 7.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </Box>
                            
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: '#d4af37', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
                            
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ 
                                fontSize: '15px', 
                                fontWeight: isSelected ? 700 : 600,
                                color: isSelected ? '#1a5a2e' : '#2a2a2a', 
                                lineHeight: 1.3,
                                mb: 0.3
                              }}>
                                {g.roomId}
                              </Typography>
                              <Typography sx={{ 
                                fontSize: '13px', 
                                color: isSelected ? '#2e7d32' : '#777',
                                lineHeight: 1.3, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}>
                                <PersonIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                                {g.guestName || 'Sin huésped asignado'}
                              </Typography>
                            </Box>
                            
                            <Chip
                              label="Boutique"
                              size="small"
                              sx={{
                                fontSize: '10px',
                                height: 20,
                                bgcolor: '#fff9e6',
                                color: '#d4af37',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )
                )}
              </Box>
            </Box>

            {/* ── Columna derecha: Precios (sin cambios) ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                px: 3, 
                py: 2, 
                borderBottom: '1px solid #e8e8e8', 
                flexShrink: 0,
                bgcolor: '#ffffff'
              }}>
                <Typography sx={{ 
                  fontSize: '13px', 
                  fontWeight: 800, 
                  letterSpacing: '0.1em',
                  color: '#666', 
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <MeetingRoomIcon sx={{ fontSize: 18, color: '#d4af37' }} />
                  PRECIOS
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={groupForm.pricing.perNight ? 'night' : 'stay'}
                    onChange={(e) => setGroupForm(f => ({ ...f, pricing: { ...f.pricing, perNight: e.target.value === 'night' } }))}
                    sx={{ 
                      borderRadius: 2, 
                      fontSize: '0.85rem', 
                      height: 34,
                      bgcolor: '#fafafa',
                      '& .MuiSelect-select': { py: 0.8 }
                    }}>
                    <MenuItem value="night" sx={{ fontSize: '0.85rem' }}>💰 Por noche</MenuItem>
                    <MenuItem value="stay" sx={{ fontSize: '0.85rem' }}>🏨 Estadía completa</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto', flex: 1 }}>
                {/* Casa Hotel */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#64b5f6', flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#555', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Casa Hotel
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[['STANDARD', 'Standard'], ['SUITE', 'Junior Suite'], ['MASTER', 'Master Suite']].map(([key, label]) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666', minWidth: 100 }}>{label}</Typography>
                        <TextField 
                          type="number" 
                          size="small" 
                          fullWidth
                          placeholder="$ 0.00"
                          value={groupForm.pricing.casaHotel[key] || ''}
                          onChange={(e) => setPricingField('casaHotel', key, e.target.value)}
                          InputProps={{ 
                            sx: { fontSize: '0.9rem', borderRadius: 2 },
                            startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>$</Typography>
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ height: '1px', bgcolor: '#e8e8e8' }} />

                {/* Hotel Boutique */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#d4af37', flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#555', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Hotel Boutique
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[['STANDARD', 'Standard'], ['SUITE', 'Junior Suite'], ['MASTER', 'Master Suite']].map(([key, label]) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666', minWidth: 100 }}>{label}</Typography>
                        <TextField 
                          type="number" 
                          size="small" 
                          fullWidth
                          placeholder="$ 0.00"
                          value={groupForm.pricing.boutique[key] || ''}
                          onChange={(e) => setPricingField('boutique', key, e.target.value)}
                          InputProps={{ 
                            sx: { fontSize: '0.9rem', borderRadius: 2 },
                            startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>$</Typography>
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fafafa' } }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Footer ── */}
          <Box sx={{
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            px: 3, 
            py: 2,
            borderTop: '1px solid #e8e8e8',
            bgcolor: '#fafafa',
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: '13px', color: '#888' }}>
              {groupForm.name && <strong style={{ color: '#333' }}>{groupForm.name}</strong>}
              {groupForm.start && <span> · 📅 {groupForm.start}</span>}
              {groupForm.end && <span> → {groupForm.end}</span>}
              {groupForm.selectedGuests.length > 0 && (
                <span style={{ color: '#25D366', fontWeight: 600, marginLeft: 4 }}> · 👥 {groupForm.selectedGuests.length} invitados</span>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button 
                onClick={() => setGroupsDialogOpen(false)}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '14px', 
                  color: '#999', 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1,
                  '&:hover': { bgcolor: '#f0f0f0' }
                }}>
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={saveGroup}
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '14px', 
                  fontWeight: 700, 
                  borderRadius: 2,
                  px: 4, 
                  py: 1, 
                  bgcolor: '#25D366', 
                  '&:hover': { bgcolor: '#20BD5A' }, 
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(37,211,102,0.3)'
                }}>
                Guardar grupo
              </Button>
            </Box>
          </Box>

        </WhatsappModalPaper>
      </Dialog>

      {/* ════════════════════════════════════════
          Diálogo: Mensajes de WhatsApp generados
      ════════════════════════════════════════ */}
      <Dialog
        open={whatsappLinks.length > 0}
        onClose={() => { setWhatsappLinks([]); setEditedWhatsappMessages({}); setDiscountDialogOpen(false); setSelectedDiscountCode(''); setSelectedAssignmentForDiscount(null); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <WhatsappModalPaper elevation={0} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, borderRadius: 3, bgcolor: '#f0fbf4', color: '#1a1a1a', border: '1px solid rgba(37,211,102,0.16)' }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 15, bgcolor: '#e8f5e9', display: 'grid', placeItems: 'center', border: '1px solid rgba(37,211,102,0.16)' }}>
              <WhatsAppIcon sx={{ fontSize: 22, color: '#25D366' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="900" sx={{ fontFamily: 'Georgia, serif' }}>Mensajes de WhatsApp</Typography>
              <Typography variant="body2" color="text.secondary">
                {whatsappLinks.length} destinatario(s). El link de reserva queda fijo y puedes ajustar el mensaje.
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2, borderRadius: 2, bgcolor: '#f0fbf4' }}>
            El mensaje predeterminado ya incluye huésped, evento, habitación, tipo, tarifa, fechas y link personalizado.
          </Alert>

          <Stack spacing={1.5} sx={{ maxHeight: 560, overflow: 'auto', pr: 1 }}>
            {whatsappLinks.map((link, index) => (
              <Paper key={index} sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(37,211,102,0.16)', bgcolor: '#ffffff',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 12px 28px rgba(0,0,0,0.08)' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 15, bgcolor: '#e8f5e9', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <WhatsAppIcon sx={{ fontSize: 22, color: '#25D366' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight="900" noWrap>{link.guestName}</Typography>
                      <Typography variant="caption" color="text.secondary">{link.phone}</Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <AnimatedActionButton variant="outlined" startIcon={<ContentCopyIcon />}
                      onClick={() => navigator.clipboard.writeText(getFullWhatsappMessage(link))}
                      sx={{ borderRadius: 2, px: 1.6, py: 0.9, minWidth: 86 }}>Copiar</AnimatedActionButton>
                    <AnimatedWhatsappButton variant="contained" href={getWhatsappOpenLink(link)} target="_blank"
                      startIcon={<WhatsAppIcon />}
                      sx={{ borderRadius: 2, px: 1.6, py: 0.9, minWidth: 86, bgcolor: '#25D366', '&:hover': { bgcolor: '#20BD5A' } }}>
                      Abrir
                    </AnimatedWhatsappButton>
                  </Stack>
                </Box>
                <TextField fullWidth multiline rows={5}
                  value={editedWhatsappMessages[link.phone] || ''}
                  onChange={(e) => handleUpdateEditedMessage(link.phone, e.target.value)}
                  placeholder="Mensaje predeterminado"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>
                  Si quitas el link, el sistema lo vuelve a agregar automáticamente al abrir WhatsApp.
                </Typography>
              </Paper>
            ))}
          </Stack>

          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.14)' }}>
            <AnimatedActionButton variant="outlined"
              onClick={() => { setWhatsappLinks([]); setEditedWhatsappMessages({}); setDiscountDialogOpen(false); setSelectedDiscountCode(''); setSelectedAssignmentForDiscount(null); }}
              sx={{ borderRadius: 2, px: 3, py: 1.1, color: '#25D366', borderColor: '#25D366', bgcolor: '#ffffff', '&:hover': { bgcolor: '#f0fbf4', borderColor: '#20BD5A' } }}>
              Cerrar
            </AnimatedActionButton>
          </DialogActions>
        </WhatsappModalPaper>
      </Dialog>

    </Container>
  );
}